/**
 * Stable IDs for player-authored content.
 *
 * RULE DCC-CUSTOM-002 - every custom content ID begins with this prefix, and no
 * printed ID contains a colon. Collision with the book is therefore impossible
 * by construction rather than by checking a list, and a glance at a save file
 * says whether something is the player's own.
 *
 * Shared by every custom content type. Skills and Backgrounds live in separate
 * lookups, so one namespace serves both.
 */
export const CUSTOM_CONTENT_ID_PREFIX = 'custom:';

export function isCustomContentId(id: string): boolean {
  return id.startsWith(CUSTOM_CONTENT_ID_PREFIX);
}

/**
 * Builds a stable ID.
 *
 * The readable part is a snapshot of the name AT CREATION and is deliberately
 * never refreshed: the ID must not move when the name is edited. It exists only
 * so the owner reading a save file can tell what a record refers to.
 *
 * The whole of `unique` is kept, not a truncation of it. A shortened suffix
 * makes a collision unlikely rather than impossible, and
 * `makeUniqueCustomContentId` is what actually guarantees uniqueness.
 */
export function makeCustomContentId(name: string, unique: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  const suffix = unique.replace(/[^a-z0-9]/gi, '').toLowerCase();
  return `${CUSTOM_CONTENT_ID_PREFIX}${slug === '' ? 'item' : slug}-${suffix === '' ? 'x' : suffix}`;
}

/**
 * Builds an ID that is certainly not already in use.
 *
 * Entropy alone is not a guarantee, so this checks and regenerates. `nextUnique`
 * is supplied by the caller rather than called for internally, which is what
 * makes the collision path testable with fixed values instead of by waiting for
 * an improbable event.
 */
export function makeUniqueCustomContentId(
  name: string,
  taken: ReadonlySet<string>,
  nextUnique: () => string,
  maxAttempts = 100,
): string {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const id = makeCustomContentId(name, nextUnique());
    if (!taken.has(id)) return id;
  }
  throw new Error('Could not generate an unused custom content ID.');
}
